"use client"

import { NavLink } from "react-router-dom"
import {
  DoorClosed,
  DoorOpen,
  Factory,
  HelpCircle,
  History,
  Home,
  Package,
  PackageSearch,
  ScanLine,
  Settings,
  ShoppingCart,
  TriangleAlert,
  Users,
} from "lucide-react"
import logo from "@/assets/logo.png"
import { cn } from "@/lib/utils.ts"
import { can } from "@/services/permissions.ts"
import { getStoredUser } from "@/services/auth-storage.ts"

/**
 * Ο συνδεδεμένος χρήστης στο υποσέλιδο του μενού.
 *
 * Ήταν σταθερά γραμμένο «Διαχειριστής» από τότε που δεν υπήρχε authentication.
 */
function SidebarUser() {
  const user = getStoredUser()
  const initial = user?.name?.trim().charAt(0).toUpperCase() || "?"

  return (
    <div className="flex items-center gap-3 px-4 py-4 border-t border-sidebar-border">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-sidebar-accent text-sidebar-accent-foreground text-sm font-semibold">
        {initial}
      </span>
      <div className="flex flex-col min-w-0">
        <p className="text-sm font-medium truncate text-foreground">{user?.name ?? "—"}</p>
        <p className="text-xs text-sidebar-muted truncate">{user?.email ?? ""}</p>
      </div>
    </div>
  )
}

// "always": ορατό σε ΚΑΘΕ συνδεδεμένο χρήστη, ανεξαρτήτως δικαιωμάτων — δεν
// περνάει καν από το can(). Μόνο ο Οδηγός Χρήσης το χρησιμοποιεί σήμερα.
type NavItem = { label: string; to: string; icon: typeof Home; end?: boolean; resource: string | "always" }
type NavGroup = { title: string; items: NavItem[] }

const groups: NavGroup[] = [
  {
    title: "Γενικά",
    items: [
      { label: "Αρχική", to: "/", icon: Home, end: true, resource: "dashboard" },
      { label: "Οδηγός Χρήσης", to: "/help", icon: HelpCircle, resource: "always" },
    ],
  },
  {
    title: "Αποθήκη",
    items: [
      { label: "Θωρακισμένες Πόρτες", to: "/inventory/security-doors", icon: DoorClosed, resource: "inventory" },
      { label: "Εσωτερικές Πόρτες", to: "/inventory/interior-doors", icon: DoorOpen, resource: "inventory" },
      // Τι λείπει συνολικά — ό,τι πρέπει να παραγγελθεί για να προχωρήσουν
      // οι δουλειές, μαζεμένο σε μία οθόνη.
      { label: "Ελλείψεις", to: "/inventory/shortages", icon: TriangleAlert, resource: "inventory" },
      { label: "Παραγγελίες Αλουμινίου", to: "/inventory/aluminium-orders", icon: PackageSearch, resource: "inventory" },
    ],
  },
  {
    title: "Παραγγελίες",
    items: [
      { label: "Θωρακισμένες Πόρτες", to: "/orders/security-doors", icon: ShoppingCart, resource: "orders" },
      { label: "Εσωτερικές Πόρτες", to: "/orders/interior-doors", icon: Package, resource: "orders" },
      // Το πάτωμα παραγωγής — φτιαγμένο για tablets στο συνεργείο. Δικό του
      // δικαίωμα (`production`), όχι `orders`: ο εργάτης παραγωγής βλέπει
      // αυτό το στοιχείο χωρίς να έχει καθόλου πρόσβαση στις Παραγγελίες.
      { label: "Παραγωγή", to: "/production", icon: Factory, resource: "production" },
      { label: "Σάρωση", to: "/scan", icon: ScanLine, resource: "scan" },
      // Υπήρχε ως route αλλά ΠΟΤΕ στο μενού: το ιστορικό ήταν προσβάσιμο μόνο
      // πληκτρολογώντας το URL.
      { label: "Ιστορικό", to: "/orders/history", icon: History, resource: "history" },
    ],
  },
  {
    title: "Διαχείριση",
    items: [
      { label: "Πελάτες", to: "/customers", icon: Users, resource: "customers" },
      { label: "Ρυθμίσεις", to: "/settings", icon: Settings, resource: "settings" },
    ],
  },
]

export function Sidebar() {
  // Ομάδα χωρίς ορατά στοιχεία δεν εμφανίζει ούτε τον τίτλο της — αλλιώς ο
  // πωλητής βλέπει επικεφαλίδα «Αποθήκη» με κενό από κάτω.
  const visible = groups
    .map((g) => ({ ...g, items: g.items.filter((i) => i.resource === "always" || can(i.resource)) }))
    .filter((g) => g.items.length > 0)

  return (
    <aside className="hidden lg:flex w-64 shrink-0 flex-col bg-sidebar text-sidebar-foreground border-r border-sidebar-border">
      <div className="flex items-center justify-center px-4 py-5 border-b border-sidebar-border">
        <img src={logo} alt="Douskas Doors" className="w-[168px] h-auto object-contain" />
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4 flex flex-col gap-5" aria-label="Κύρια πλοήγηση">
        {visible.map((group) => (
          <div key={group.title} className="flex flex-col gap-1">
            <p className="px-2.5 text-[11px] font-semibold uppercase tracking-wider text-sidebar-muted">
              {group.title}
            </p>
            {group.items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13px] font-medium transition-colors",
                    isActive
                      ? "bg-sidebar-accent text-sidebar-accent-foreground"
                      : "text-sidebar-foreground hover:bg-muted hover:text-foreground",
                  )
                }
              >
                <item.icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                <span className="truncate">{item.label}</span>
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      <SidebarUser />
    </aside>
  )
}

const mobileItems: NavItem[] = [
  { label: "Αρχική", to: "/", icon: Home, end: true, resource: "dashboard" },
  { label: "Αποθήκη", to: "/inventory/security-doors", icon: Package, resource: "inventory" },
  { label: "Παραγγελίες", to: "/orders/security-doors", icon: ShoppingCart, resource: "orders" },
  // Πολλά tablets πέφτουν κάτω από το `lg` σε portrait — το κάτω μενού είναι
  // ο μόνος τρόπος να φτάσει κανείς εδώ σε αυτή τη διάταξη.
  { label: "Παραγωγή", to: "/production", icon: Factory, resource: "production" },
  { label: "Σάρωση", to: "/scan", icon: ScanLine, resource: "scan" },
  { label: "Πελάτες", to: "/customers", icon: Users, resource: "customers" },
  { label: "Ρυθμίσεις", to: "/settings", icon: Settings, resource: "settings" },
  { label: "Οδηγός", to: "/help", icon: HelpCircle, resource: "always" },
]

export function MobileNav() {
  return (
    <nav
      className="lg:hidden fixed bottom-0 inset-x-0 z-40 flex items-stretch justify-around bg-sidebar text-sidebar-foreground border-t border-sidebar-border"
      aria-label="Πλοήγηση κινητού"
    >
      {mobileItems.filter((i) => i.resource === "always" || can(i.resource)).map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          className={({ isActive }) =>
            cn(
              "flex flex-1 flex-col items-center gap-1 py-2.5 text-[10px] font-medium transition-colors",
              isActive ? "text-primary" : "text-sidebar-muted",
            )
          }
        >
          <item.icon className="h-5 w-5" aria-hidden="true" />
          <span>{item.label}</span>
        </NavLink>
      ))}
    </nav>
  )
}
